import { Stack } from '@festina-life/ui/structure'

import { configurationItem } from '../../system/paths'
import { useNavigationParams } from '../../system/useNavigationParams'
import { getReconciliationSpecOperationFromDate } from '../../uiModels/reconciliationSpecOperations/helpers'
import type { IndexedReconciliationSpecOperationWithMetaData } from '../../uiModels/reconciliationSpecOperations/types'
import { fromDateAsLocalDate } from '../../util/versionDateUtils'
import { useOperationsContext } from '../../workspace/operationsContext'
import { OperationVersionTooltipEntry } from '../versionedTimeline/OperationVersionTooltipEntry'
import { useIsReconciliationSpecVersionActive } from './useIsReconciliationSpecVersionActive'

type Props = {
  versions: IndexedReconciliationSpecOperationWithMetaData[]
  earliestVersionDate: Date
}

/**
 * Describes the reconciliation-spec versions sharing a date on the timeline. Versions are
 * selected by changing the view date rather than by navigation, so no link is offered.
 */
export const ReconciliationSpecVersionsTooltipContent = ({
  versions,
  earliestVersionDate,
}: Props) => {
  const { workspaceKey, branchKey } = useOperationsContext()
  const { server } = useNavigationParams()
  const isVersionActive = useIsReconciliationSpecVersionActive()

  return (
    <Stack className="flex-col">
      {versions.map((version) => {
        const fromDate = version.internalOperation.reconciliationSpec?.from
        const fromDateAsDate = fromDateAsLocalDate(fromDate)

        const identifier =
          version.internalOperation.reconciliationSpec?.specId ?? '-'

        return (
          <OperationVersionTooltipEntry
            key={version.internalOperation.operationKey}
            identifier={identifier}
            isActive={isVersionActive(version)}
            isDraft={version.isDraft}
            isInherited={false}
            fromDate={getReconciliationSpecOperationFromDate(
              version.internalOperation,
            )}
            changesetHistory={version.changesetHistory}
            layer={version.layer}
            block={version.block}
            to={{
              path: configurationItem({
                block: version.block,
                layer: version.layer,
                itemId: identifier,
                type: 'reconciliationSpec',
                workspaceName: workspaceKey?.name,
                branch: branchKey?.name,
                server: workspaceKey?.branchKey.server ?? server,
                untilChangesetKey: version.changesetHistory?.changesetKey,
              }),
              atTime: fromDateAsDate ?? earliestVersionDate,
            }}
          />
        )
      })}
    </Stack>
  )
}
